import { useState, useRef, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { MessageCircle, X, RotateCcw } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useChatBot } from '@/contexts/ChatBotContext'
import { useBackgroundContrast } from '@/hooks/useBackgroundContrast'
import { ChatMessage } from './ChatMessage'
import { ChatInput } from './ChatInput'
import { QuickSuggestions } from './QuickSuggestions'
import { quickSuggestions } from './config'
import type { ChatMessage as ChatMessageType, ChatState } from '@/types/chatbot'

export function ChatBot() {
  const { t, i18n } = useTranslation('common')
  const { isOpen, setIsOpen } = useChatBot()
  const [messages, setMessages] = useState<ChatMessageType[]>([])
  const [chatState, setChatState] = useState<ChatState>('idle')
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const floatingButtonRef = useRef<HTMLButtonElement>(null)
  const background = useBackgroundContrast(floatingButtonRef)
  const isDarkBackground = background === 'dark'

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  useEffect(() => {
    scrollToBottom()
  }, [messages, scrollToBottom])

  useEffect(() => {
    if (isOpen) {
      // Focus input when chat opens
      setTimeout(() => inputRef.current?.focus(), 300)
    }
  }, [isOpen])

  const generateId = () =>
    `msg_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`

  const sendMessage = async (content: string) => {
    if (!content.trim() || chatState === 'loading') return

    const userMessage: ChatMessageType = {
      id: generateId(),
      role: 'user',
      content: content.trim(),
      timestamp: new Date(),
    }

    setMessages((prev) => [...prev, userMessage])
    setChatState('loading')

    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: content,
          history: messages.map((m) => ({ role: m.role, content: m.content })),
          language: i18n.language,
        }),
      })

      const data = await response.json()

      const assistantMessage: ChatMessageType = {
        id: generateId(),
        role: 'assistant',
        content: data.response || data.error || t('chatbot.error'),
        timestamp: new Date(data.timestamp || Date.now()),
      }

      setMessages((prev) => [...prev, assistantMessage])
      setChatState('idle')
    } catch (error) {
      console.error('Chat error:', error)
      setChatState('error')

      const errorMessage: ChatMessageType = {
        id: generateId(),
        role: 'assistant',
        content: t('chatbot.error'),
        timestamp: new Date(),
      }
      setMessages((prev) => [...prev, errorMessage])
    }
  }

  const handleReset = () => {
    setMessages([])
    setChatState('idle')
  }

  const handleSuggestionSelect = (messageKey: string) => {
    const message = t(messageKey)
    sendMessage(message)
  }

  return (
    <div ref={containerRef} className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-tooltip">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            transition={{ duration: 0.2 }}
            className={cn(
              'absolute bottom-16 sm:bottom-20 right-0',
              'w-[calc(100vw-32px)] sm:w-[380px] max-w-[380px]',
              'bg-white rounded-lg shadow-xl overflow-hidden',
              'flex flex-col',
              'max-h-[calc(100vh-100px)] sm:max-h-[calc(100vh-120px)]'
            )}
          >
            {/* Header */}
            <div className="bg-[#222222] text-white p-4 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-lg text-white">{t('chatbot.title')}</h3>
                <p className="text-sm opacity-80">{t('chatbot.subtitle')}</p>
              </div>
              <button
                onClick={handleReset}
                className="p-2 hover:bg-white/10 rounded-full transition-colors"
                aria-label={t('chatbot.newConversation')}
                title={t('chatbot.newConversation')}
              >
                <RotateCcw className="w-5 h-5" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-[200px] max-h-[400px] bg-[#f7f7f7]">
              {messages.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  <p className="mb-6">{t('chatbot.welcome')}</p>
                  <QuickSuggestions
                    suggestions={quickSuggestions}
                    onSelect={handleSuggestionSelect}
                    disabled={chatState === 'loading'}
                  />
                </div>
              ) : (
                <>
                  {messages.map((message) => (
                    <ChatMessage key={message.id} message={message} />
                  ))}
                  {chatState === 'loading' && (
                    <ChatMessage
                      message={{
                        id: 'typing',
                        role: 'assistant',
                        content: '',
                        timestamp: new Date(),
                      }}
                      isTyping
                    />
                  )}
                </>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <ChatInput
              ref={inputRef}
              onSend={sendMessage}
              disabled={chatState === 'loading'}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Button */}
      <motion.button
        ref={floatingButtonRef}
        onClick={() => setIsOpen(!isOpen)}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        className="w-14 h-14 sm:w-16 sm:h-16 rounded-full shadow-lg flex items-center justify-center transition-colors"
        style={{ backgroundColor: isDarkBackground ? '#ffffff' : '#e5ae49' }}
        aria-label={isOpen ? t('chatbot.close') : t('chatbot.open')}
      >
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.div
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
            >
              <X className="w-6 h-6 sm:w-7 sm:h-7 text-[#222222]" />
            </motion.div>
          ) : (
            <motion.div
              key="chat"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
            >
              <MessageCircle className="w-6 h-6 sm:w-7 sm:h-7 text-[#222222]" />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>
    </div>
  )
}
